import React from 'react';
import { useRouter } from 'next/router';

import { IconName } from 'components';
import SlideShow from 'components/SlideShow';
import { CompanyCard } from 'components/ComponentV2/CompanyCard/CompanyCard.component';
import { ActionMenuButton } from 'components/ActionMenuButton/ActionMenuButton.component';
import { parseResponse } from 'lib/utils/parser';
import { TagAttributes, TagType } from 'lib/models/tag';
import { PAGE_ROUTES } from 'lib/page-routes';
import { CompanyAttributes, PER_PAGE_COUNT } from 'lib/models/company';
import { useSponsorCompanies } from 'lib/providers/SocietySponsorshipProvider';
import { useSponsorshipPlans } from '../useSponsorshipPlans';

interface RenderCompaniesDataCardsProps {
  displaySlideshow?: boolean;
  onMessageCompany: (company: CompanyAttributes) => void;
  onAddSponsor: (company: CompanyAttributes) => void;
  onSelectPlan: () => void;
  onInviteSponsor: (company: CompanyAttributes) => void;
  sliderClassName?: string;
  navigationClassName?: string;
}

export const RenderCompaniesDataCards: React.FC<RenderCompaniesDataCardsProps> =
  (props: RenderCompaniesDataCardsProps) => {
    const {
      displaySlideshow,
      sliderClassName,
      navigationClassName,
      onMessageCompany,
      onAddSponsor,
      onSelectPlan,
      onInviteSponsor,
    } = props;
    const router = useRouter();
    const {
      companiesData,
      sponsorCompanyIds,
      sponsorRequestCompanyIds,
      messageCompanyIds,
    } = useSponsorCompanies();
    const { sponsorshipPlans } = useSponsorshipPlans();

    const addSponsorAction = (company: CompanyAttributes) => {
      if (sponsorshipPlans && sponsorshipPlans.length) {
        onAddSponsor(company);
      } else {
        onSelectPlan();
      }
    };

    const getActionOptions = (company: CompanyAttributes) => {
      const messageOption = {
        id: '1',
        label: 'Message',
        onClick: () => onMessageCompany(company),
      };

      const options = [
        {
          id: '2',
          label: 'Add as sponsor',
          onClick: () =>
            company.is_verified
              ? addSponsorAction(company)
              : onInviteSponsor(company),
        },
      ];

      return company.is_verified ? [messageOption, ...options] : options;
    };

    const getButtonProps = (companyId: string) => {
      let label: string = 'Add';
      let showArrow: boolean = false;
      let disableBtn: boolean = false;
      let startIcon: IconName | undefined = undefined;
      if (sponsorCompanyIds.includes(companyId)) {
        disableBtn = true;
        startIcon = 'icon_check-double';
        label = 'Sponsoring';
      } else if (sponsorRequestCompanyIds.includes(companyId)) {
        showArrow = true;
        label = 'Request sent';
      } else if (messageCompanyIds.includes(companyId)) {
        startIcon = 'icon_check';
        showArrow = true;
        label = 'Messaged';
      }
      return { label, showArrow, disableBtn, startIcon };
    };

    const renderCompanyCard = (company: CompanyAttributes) => {
      const {
        id,
        icon_url,
        name,
        industry: industryResponse,
        huzzle_verified,
        is_verified,
        slug,
      } = company;
      const industry =
        parseResponse<TagAttributes<TagType.Industry>>(industryResponse);

      return (
        <CompanyCard
          key={id}
          icon={icon_url}
          name={name}
          title={name}
          caption={industry?.name}
          isHuzzleVerified={huzzle_verified}
          isVerified={is_verified}
          onSelect={() => router.push(`${PAGE_ROUTES.COMPANIES}/${slug}`)}
        >
          {renderActionButton(company)}
        </CompanyCard>
      );
    };

    const renderActionButton = (company: CompanyAttributes) => {
      const { label, showArrow, disableBtn, startIcon } = getButtonProps(
        company.id
      );

      return (
        <ActionMenuButton
          options={getActionOptions(company)}
          buttonText={label}
          buttonColor="secondary"
          buttonSize="small"
          dropdownDirection="top"
          disabled={disableBtn}
          buttonStartIcon={startIcon}
          buttonEndIcon={showArrow ? 'icon_arrow-down' : undefined}
        />
      );
    };

    return (
      <>
        {displaySlideshow ? (
          <SlideShow
            slidesPerRow={4}
            gutter={16}
            maxWidth="254px"
            width={254}
            partialSlide={0.5}
            className={sliderClassName}
            navigationClassName={navigationClassName}
          >
            {companiesData &&
              companiesData
                .slice(0, PER_PAGE_COUNT)
                .map((company) => renderCompanyCard(company))}
          </SlideShow>
        ) : (
          <>
            {companiesData &&
              companiesData.map((company) => renderCompanyCard(company))}
          </>
        )}
      </>
    );
  };
